import { useMemo } from 'react';
import { DataTable } from '../components/DataTable';
import { TablePageLayout } from '../components/TablePageLayout';
import { StatusBadge } from '../components/StatusBadge';
import { fetchDashboardPurchases } from '../api/dashboardApi';
import { useQuery } from '@tanstack/react-query';
import { formatDate, formatCurrency } from '../utils/formatters';
import { useScrollRestoration } from '../hooks/useScrollRestoration';

const stateColors = {
  purchase: 'success',
  done: 'primary',
  cancel: 'danger',
  draft: 'secondary',
  sent: 'info',
  to_approve: 'warning',
};

const stateLabels = {
  purchase: { emoji: '✅', label: 'Purchase Order' },
  done: { emoji: '✅', label: 'Locked' },
  cancel: { emoji: '❌', label: 'Cancelled' },
  draft: { emoji: '📝', label: 'Quotation' },
  sent: { emoji: '📧', label: 'Quotation Sent' },
  to_approve: { emoji: '⏳', label: 'To Approve' },
};

const invoiceStatusColors = {
  'to invoice': 'warning',
  'invoiced': 'success',
  'no': 'secondary',
};

const invoiceStatusLabels = {
  'to invoice': { emoji: '⏳', label: 'To Bill' },
  'invoiced': { emoji: '✅', label: 'Fully Billed' },
  'no': { emoji: '🚫', label: 'Nothing to Bill' },
};


export function PurchaseIssuesPage() {
  const { data, isLoading, isError, error, isFetched } = useQuery({
    queryKey: ['dashboard-purchases'],
    queryFn: fetchDashboardPurchases,
  });

  const { containerRef, handleScroll } = useScrollRestoration(
    'purchaseIssuesListScroll',
    isFetched
  );

  const rows = useMemo(() => {
    if (!data || !data.orders) return [];

    return data.orders.map((order) => [
      <a href={order.url} target="_blank" rel="noreferrer">{order.name}</a>,
      formatDate(order.date_order),
      order.user_id?.name || '-',
      formatCurrency(order.amount_total),
      <StatusBadge value={order.invoice_status} colors={invoiceStatusColors} labels={invoiceStatusLabels} />
    ]);
  }, [data]);

  return (
    <TablePageLayout
      title="Purchases not fully billed"
      description="Purchase orders with invoice coverage below one hundred percent."
      isLoading={isLoading}
      isError={isError}
      error={error}
      containerRef={containerRef}
      onScroll={handleScroll}
    >
      <DataTable
        headers={['Reference', 'Order date', 'Responsible', 'Total', 'Percent invoiced']}
        rows={rows}
        className="sticky-header"
      />
    </TablePageLayout>
  );
}
